import { useState, useEffect, useCallback } from 'react';
import { enhancedStatisticsService } from '../services/enhancedStatisticsService';

export interface SessionData {
  id: number;
  date: string;
  wordCount: number;
  charCount: number;
  sessionDuration?: number;
  deletedChars?: number;
  deletedWords?: number;
  numTimesDeleto?: number;
  sessionEndedEarly?: boolean;
  sessionCompleted?: boolean;
}

export interface DayStats {
  date: string;
  sessions: number;
  totalWords: number;
  totalChars: number;
  totalTime: number;
  deletedChars: number;
  deletedWords: number;
  numTimesDeleto: number;
}

export interface StatsSummary {
  totalSessions: number;
  totalWords: number;
  totalCharacters: number;
  totalTime: number;
  totalDeletedChars: number;
  totalDeletedWords: number;
  totalTimesDeleto: number;
  averageWordsPerSession: number;
  averageCharsPerSession: number;
  averageSessionLength: number;
  longestSession: number;
  mostProductiveDay: DayStats | null;
  currentStreak: number;
  longestStreak: number;
  wordsPerMinute: number;
}

export function useStatistics() {
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [dailyStats, setDailyStats] = useState<DayStats[]>([]);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const calculateStreak = useCallback((dailyStats: DayStats[]): { current: number; longest: number } => {
    if (dailyStats.length === 0) return { current: 0, longest: 0 };

    // Sort by date descending (most recent first)
    const toDateLocal = (dateStr: string): Date => {
      // Parse YYYY-MM-DD as a local date to avoid UTC shifting
      const m = /^\d{4}-\d{2}-\d{2}$/.exec(dateStr);
      if (m) {
        const [y, mth, d] = dateStr.split('-').map(Number);
        return new Date(y, mth - 1, d);
      }
      return new Date(dateStr);
    };

    const sortedStats = [...dailyStats].sort((a, b) => toDateLocal(b.date).getTime() - toDateLocal(a.date).getTime());
    
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;
    
    // Calculate current streak (from today backwards)
    // Build today's date string in the user's local timezone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const today = new Intl.DateTimeFormat('en-CA', {
      timeZone: userTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date());
    const todayStats = sortedStats.find(stat => stat.date === today);
    
    if (todayStats && todayStats.sessions > 0) {
      currentStreak = 1;
      // Look backwards for consecutive days
      for (let i = 1; i < sortedStats.length; i++) {
        const currentDate = toDateLocal(sortedStats[i].date);
        const previousDate = toDateLocal(sortedStats[i-1].date);
        const dayDiff = Math.abs(previousDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);
        
        if (dayDiff === 1 && sortedStats[i].sessions > 0) {
          currentStreak++;
        } else {
          break;
        }
      }
    }
    
    // Calculate longest streak ever
    for (let i = 0; i < sortedStats.length; i++) {
      if (sortedStats[i].sessions > 0) {
        tempStreak = 1;
        for (let j = i + 1; j < sortedStats.length; j++) {
          const currentDate = toDateLocal(sortedStats[j].date);
          const previousDate = toDateLocal(sortedStats[j-1].date);
          const dayDiff = Math.abs(previousDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);
          
          if (dayDiff === 1 && sortedStats[j].sessions > 0) {
            tempStreak++;
          } else {
            break;
          }
        }
        longestStreak = Math.max(longestStreak, tempStreak);
        i += tempStreak - 1; // Skip the days we just counted
      }
    }
    
    return { current: currentStreak, longest: longestStreak };
  }, []);

  const loadStatistics = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // Get sessions from enhanced statistics service
      const sessions: SessionData[] = await enhancedStatisticsService.getSessions();
      
      if (sessions.length === 0) {
        setStats({
          totalSessions: 0,
          totalWords: 0,
          totalCharacters: 0,
          totalTime: 0,
          totalDeletedChars: 0,
          totalDeletedWords: 0,
          totalTimesDeleto: 0,
          averageWordsPerSession: 0,
          averageCharsPerSession: 0,
          averageSessionLength: 0,
          longestSession: 0,
          mostProductiveDay: null,
          currentStreak: 0,
          longestStreak: 0,
          wordsPerMinute: 0
        });
        setDailyStats([]);
        setSessions([]);
        setIsLoading(false);
        return;
      }

      // Group sessions by day
      const dailyStatsMap = new Map<string, DayStats>();
      
      // Helper: format a UTC timestamp into a local YYYY-MM-DD string using the user's timezone
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const toLocalYmd = (ts: string): string => {
        // Use Intl to format without time; en-CA -> YYYY-MM-DD
        return new Intl.DateTimeFormat('en-CA', {
          timeZone: userTimeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(new Date(ts));
      };

      sessions.forEach(session => {
        const date = toLocalYmd(session.date);
        const existing = dailyStatsMap.get(date);
        
        if (existing) {
          existing.sessions += 1;
          existing.totalWords += session.wordCount || 0;
          existing.totalChars += session.charCount || 0;
          existing.totalTime += session.sessionDuration || 0;
          existing.deletedChars += session.deletedChars || 0;
          existing.deletedWords += session.deletedWords || 0;
        } else {
          dailyStatsMap.set(date, {
            date,
            sessions: 1,
            totalWords: session.wordCount || 0,
            totalChars: session.charCount || 0,
            totalTime: session.sessionDuration || 0,
            deletedChars: session.deletedChars || 0,
            deletedWords: session.deletedWords || 0,
            numTimesDeleto: session.numTimesDeleto || 0
          });
        }
      });

      const toDateLocal = (dateStr: string): Date => {
        const m = /^\d{4}-\d{2}-\d{2}$/.exec(dateStr);
        if (m) {
          const [y, mth, d] = dateStr.split('-').map(Number);
          return new Date(y, mth - 1, d);
        }
        return new Date(dateStr);
      };

      const dailyStatsArray = Array.from(dailyStatsMap.values()).sort((a, b) => 
        toDateLocal(b.date).getTime() - toDateLocal(a.date).getTime()
      );

      // Calculate summary statistics
      const totalSessions = sessions.length;
      const totalWords = sessions.reduce((sum, s) => sum + (s.wordCount || 0), 0);
      const totalCharacters = sessions.reduce((sum, s) => sum + (s.charCount || 0), 0);
      const totalTime = sessions.reduce((sum, s) => sum + (s.sessionDuration || 0), 0);
      const totalDeletedChars = sessions.reduce((sum, s) => sum + (s.deletedChars || 0), 0);
      const totalDeletedWords = sessions.reduce((sum, s) => sum + (s.deletedWords || 0), 0);
      const totalTimesDeleto = sessions.reduce((sum, s) => sum + (s.numTimesDeleto || 0), 0);
      
      const averageWordsPerSession = totalSessions > 0 ? Math.round(totalWords / totalSessions) : 0;
      const averageCharsPerSession = totalSessions > 0 ? Math.round(totalCharacters / totalSessions) : 0;
      const averageSessionLength = totalSessions > 0 ? Math.round(totalTime / totalSessions) : 0;
      
      const longestSession = sessions.reduce((max, s) => Math.max(max, s.sessionDuration || 0), 0);
      
      const mostProductiveDay = dailyStatsArray.reduce((max, day) => 
        !max || day.totalWords > max.totalWords ? day : max, null as DayStats | null
      );

      const streaks = calculateStreak(dailyStatsArray);
      
      const wordsPerMinute = totalTime > 0 ? Math.round((totalWords / totalTime) * 60) : 0;

      const summaryStats: StatsSummary = {
        totalSessions,
        totalWords,
        totalCharacters,
        totalTime,
        totalDeletedChars,
        totalDeletedWords,
        totalTimesDeleto,
        averageWordsPerSession,
        averageCharsPerSession,
        averageSessionLength,
        longestSession,
        mostProductiveDay,
        currentStreak: streaks.current,
        longestStreak: streaks.longest,
        wordsPerMinute
      };

      setStats(summaryStats);
      setDailyStats(dailyStatsArray);
      setSessions(sessions);
      
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setIsLoading(false);
    }
  }, [calculateStreak]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const refreshStats = useCallback(() => {
    loadStatistics();
  }, [loadStatistics]);

  const formatTime = useCallback((seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600 * 10) / 10}h`;
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  return {
    stats,
    dailyStats,
    sessions,
    isLoading,
    refreshStats,
    formatTime,
    formatDate
  };
} 